import React from 'react';
import styled from 'styled-components';

const StyledButton = styled.button`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  letter-spacing: ${({ theme }) => theme.letterSpacing.wide};
  text-transform: uppercase;
  
  padding: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '0.5rem 1rem';
      case 'large':
        return '1rem 2rem';
      default:
        return '0.75rem 1.5rem';
    }
  }};
  
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all ${({ theme }) => theme.duration.fast}ms ${({ theme }) => theme.easing.easeOut};
  
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%);
          color: ${theme.colors.text.inverse};
          box-shadow: 0 4px 6px ${theme.colors.shadow.medium};
          
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 12px ${theme.colors.shadow.dark};
          }
          
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      case 'secondary':
        return `
          background: transparent;
          color: ${theme.colors.primary.main};
          border-color: ${theme.colors.primary.main};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.primary.main}10;
            transform: translateY(-2px);
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.text.primary};
          border-color: ${theme.colors.border.dark};
          
          &:hover:not(:disabled) {
            border-color: ${theme.colors.primary.main};
            color: ${theme.colors.primary.main};
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: ${theme.colors.text.primary};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.background.paper};
          }
        `;
      case 'thermal':
        return `
          background: linear-gradient(135deg, ${theme.colors.thermal.cool} 0%, ${theme.colors.thermal.warm} 100%);
          color: ${theme.colors.text.inverse};
          box-shadow: 0 4px 6px ${theme.colors.shadow.medium};
          
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 0 20px ${theme.colors.thermal.warm}60;
          }
        `;
      default:
        return '';
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
  
  ${({ $fullWidth }) => $fullWidth && `
    width: 100%;
  `}
  
  ${({ $iconOnly }) => $iconOnly && `
    padding: 0.75rem;
    width: 40px;
    height: 40px;
  `}
`;

const Button = React.forwardRef(
  ({ children, variant = 'primary', size = 'medium', fullWidth = false, iconOnly = false, startIcon, endIcon, disabled = false, ...props }, ref) => {
    return (
      <StyledButton
        ref={ref}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $iconOnly={iconOnly}
        disabled={disabled}
        {...props}
      >
        {startIcon && !iconOnly && <span className="start-icon">{startIcon}</span>}
        {!iconOnly && children}
        {endIcon && !iconOnly && <span className="end-icon">{endIcon}</span>}
        {iconOnly && children}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;